import React, { useEffect, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./SubscriptionPage.css";

const SubscriptionPage = () => {
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [undoing, setUndoing] = useState(false);

    // 🔒 Local UI lock to prevent flicker
    // null = trust backend, true/false = temporary UI override
    const [localCancelOverride, setLocalCancelOverride] = useState(null);

    /* =========================
       FETCH SUBSCRIPTION STATUS
    ========================= */
    const fetchStatus = async () => {
        try {
            const res = await apiFetch("/api/subscription/status");


            if (!res.ok) throw new Error("Failed to load subscription");

            const data = await res.json();
            setSubscription(data);
            setError(null);
        } catch {
            setError("Unable to load subscription");
        } finally {
            setLoading(false);
        }
    };

    /* =========================
       INITIAL LOAD
    ========================= */
    useEffect(() => {
        fetchStatus();
    }, []);

    /* =========================
       🔄 RESYNC WHEN RETURNING
       FROM STRIPE PORTAL
    ========================= */
    useEffect(() => {
        const onFocus = () => {
            fetchStatus(); // Stripe → Webhook → DB → UI
        };

        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, []);

    /* =========================
       CANCEL SUBSCRIPTION
    ========================= */
    const handleCancel = async () => {
        try {
            setCanceling(true);

            const res = await apiFetch("/api/subscription/cancel", {
                method: "POST",
            });


            if (!res.ok) throw new Error("Cancel failed");

            // Optimistic UI lock
            setLocalCancelOverride(true);
            setSubscription(prev => ({
                ...prev,
                cancelAtPeriodEnd: true,
            }));

            setShowModal(false);
            setTimeout(fetchStatus, 1500);
        } catch {
            alert("Failed to cancel subscription.");
        } finally {
            setCanceling(false);
        }
    };

    /* =========================
       UNDO CANCELLATION
    ========================= */
    const handleUndoCancel = async () => {
        try {
            setUndoing(true);

            const res = await apiFetch("/api/subscription/undo-cancel", {
                method: "POST",
            });

            if (!res.ok) throw new Error("Undo failed");

            // Hard UI lock
            setLocalCancelOverride(false);
            setSubscription(prev => ({
                ...prev,
                cancelAtPeriodEnd: false,
            }));

            setTimeout(fetchStatus, 1500);
        } catch {
            alert("Failed to undo cancellation.");
        } finally {
            setUndoing(false);
        }
    };

    /* =========================
       RELEASE UI LOCK
       ON BACKEND SYNC
    ========================= */
    useEffect(() => {
        if (
            localCancelOverride !== null &&
            subscription?.cancelAtPeriodEnd === localCancelOverride
        ) {
            setLocalCancelOverride(null);
        }
    }, [subscription, localCancelOverride]);

    /* =========================
       LOADING / ERROR
    ========================= */
    if (loading) return <div className="subscription-page">Loading…</div>;
    if (error || !subscription)
        return <div className="subscription-page error">{error}</div>;

    /* =========================
       🚫 BYPASS (PLAYERS / ADMINS)
    ========================= */
    if (subscription.required === false) {
        return (
            <div className="subscription-page">
                <h2>Subscription</h2>
                <div className="subscription-card">
                    <p style={{ textAlign: "center" }}>
                        This account does not require a subscription.
                    </p>
                </div>
            </div>
        );
    }

    /* =========================
       STATUS CALCULATION
    ========================= */
    const { expiresAt } = subscription;

    const cancelAtPeriodEnd =
        localCancelOverride !== null
            ? localCancelOverride
            : subscription.cancelAtPeriodEnd;

    const now = Date.now();
    const expiresTime = expiresAt ? new Date(expiresAt).getTime() : null;

    const isExpired = Boolean(expiresTime && now >= expiresTime);
    const isCanceling = cancelAtPeriodEnd && !isExpired;
    const isActive = !isExpired && !isCanceling;

    const statusLabel = isExpired
        ? "Expired"
        : isCanceling
            ? "Canceling"
            : "Active";

    const formattedDate = expiresAt
        ? new Date(expiresAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
        : null;

    /* =========================
       UI
    ========================= */
    return (
        <div className="subscription-page">
            <h2>Subscription</h2>

            {isExpired && (
                <div className="expired-banner">
                    Your subscription has expired. Access is locked.
                </div>
            )}

            <div className="subscription-card">
                <div className="subscription-row">
                    <span className="label">Plan</span>
                    <span className="value">Dashboard Access Subscription</span>
                </div>

                <div className="subscription-row">
                    <span className="label">Price</span>
                    <span className="value">$50 / month</span>
                </div>

                <div className="subscription-row">
                    <span className="label">Status</span>
                    <span className={`value status-${statusLabel.toLowerCase()}`}>
                        {statusLabel}
                    </span>
                </div>

                {isCanceling && (
                    <div className="cancel-note">
                        {formattedDate ? (
                            <>
                                Access remains active until{" "}
                                <strong>{formattedDate}</strong>.
                            </>
                        ) : (
                            <>Access remains active until the end of your billing period.</>
                        )}
                    </div>
                )}

                {!isCanceling && formattedDate && (
                    <div className="subscription-row">
                        <span className="label">Renews on</span>
                        <span className="value">{formattedDate}</span>
                    </div>
                )}

                <div className="subscription-actions">
                    {!isCanceling && (
                        <button
                            className="cancel-btn"
                            disabled={!isActive || canceling}
                            onClick={() => setShowModal(true)}
                        >
                            Cancel subscription
                        </button>
                    )}

                    {isCanceling && (
                        <button
                            className="undo-btn"
                            disabled={undoing}
                            onClick={handleUndoCancel}
                        >
                            {undoing ? "Undoing…" : "Undo cancellation"}
                        </button>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="modal-backdrop">
                    <div className="modal">
                        <h3>Cancel subscription?</h3>
                        <p>
                            Your access will remain active until the end of your
                            billing period.
                        </p>

                        <div className="modal-actions">
                            <button
                                className="modal-btn secondary"
                                onClick={() => setShowModal(false)}
                            >
                                Keep subscription
                            </button>
                            <button
                                className="modal-btn danger"
                                onClick={handleCancel}
                                disabled={canceling}
                            >
                                {canceling ? "Canceling…" : "Confirm cancel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionPage;






